/**
 * チャートツールチップの表示値フォーマット (純関数)。
 *
 * kWh 系列 (需要ドメイン、左軸) は表示テキストを四捨五入した整数にする
 * (docs/specs/README.md §数値表示共通ルール「kWh は整数 (カンマ区切り)」/
 * design/demand/forecast-today.html `_dftTooltipContent` L5645 の
 * `Math.round(v).toLocaleString()` 準拠)。託送実績由来の系列 (todayActual /
 * past_N) は送電端換算で小数を持つため、丸めずに出すと生値が並ぶ。
 * 丸めるのは表示テキストのみで、系列の描画値 (プロット位置) は小数を保持する
 * (doji-bg.md AC-BDB-029a / kwhDisplay.ts の godoc と同じ考え方)。
 *
 * 気温・露点系列 (℃、右軸 yAxisId="temp") は丸めず入力精度のまま表示する
 * (design 同関数 L5643 `v + ' ℃'` 準拠で ℃ は素通し)。
 */
import { komaNumberToTime } from '~/models/komaToTime'
import { formatKwh } from '~/models/kwhDisplay'

/**
 * チャート末尾のダミー点の X ラベル。
 *
 * 後端を 24:00 まで伸ばすために置いている点で、需要系列は最終コマ (48) の値を
 * 複製している (`ForecastChart` の `atKoma`)。気温系列は複製せず index 48 を
 * そのまま読むので、予測気温は対象日が明日以前なら 24 時の点を持つ。この点で
 * ツールチップを出すと
 * 「コマ 49」という domain 外のコマ番号 (spec のコマ番号は 1〜48) と、
 * コマ 48 と同じ値の重複行が出てしまう。design も同じ理由で
 * `_dftTooltipContent` の冒頭で握り潰している
 * (`demand/forecast-today.html`「末尾 24:00⁺ dummy 点はマウスオーバー対象外」)。
 */
export const CHART_TAIL_DUMMY_LABEL = '24:00'

/** チャート終端 (翌日 3:00) の点の X ラベル。stepAfter で翌日 koma 6 の線を 3:00 まで引くための点。 */
export const CHART_END_LABEL = '27:00'

/**
 * 後端 (翌日 0:00〜) の X ラベル ("24:00"〜"27:00" の 30 時間制) を翌日の時刻 "HH:MM" に直す。
 * 後端以外は undefined。ツールチップの見出しを対象日のコマ番号 (コマ 49 等) にしないために使う。
 */
export function nextDayExtensionTime(
  label: string | number | undefined,
): string | undefined {
  if (typeof label !== 'string') return undefined
  const m = /^(\d{2}):(\d{2})$/.exec(label)
  if (m === null) return undefined
  const hour = Number(m[1])
  if (hour < 24) return undefined
  return `${String(hour - 24).padStart(2, '0')}:${m[2] ?? '00'}`
}

/** そのラベルの点でツールチップを出さないか。 */
export function isChartTailDummyLabel(
  label: string | number | undefined,
): boolean {
  return label === CHART_TAIL_DUMMY_LABEL
}

/** 延長部の需要想定の参考系列 (AC-DFD-006a-1) の key。 */
export const EXTENSION_FORECAST_PREV_KEY = 'forecastPrev'
export const EXTENSION_FORECAST_NEXT_KEY = 'forecastNext'

/**
 * ツールチップに出す行。
 *
 * - 末尾 24:00 のダミー点: 翌日 koma 1 の値を持つ `forecastNext` の行だけを出す
 *   (無ければ空 = ツールチップを出さない。他の系列は [[CHART_TAIL_DUMMY_LABEL]] の理由で出さない)
 * - 終端 27:00 の点: `forecastNext` は翌日 koma 6 を線の終端として複製した値なので出さない
 * - 参考系列が需要想定の線につなぐために対象日の値を持つ点 (0:00 の `forecastPrev`、
 *   23:30 の `forecastNext`) では、その参考系列の行を出さない (需要想定と同じ値の重複行になるため)
 */
export function visibleTooltipEntries<T extends { dataKey?: string | number }>(
  label: string | number | undefined,
  entries: readonly T[],
): T[] {
  if (isChartTailDummyLabel(label))
    return entries.filter((e) => e.dataKey === EXTENSION_FORECAST_NEXT_KEY)
  if (label === CHART_END_LABEL)
    return entries.filter((e) => e.dataKey !== EXTENSION_FORECAST_NEXT_KEY)
  if (label === komaNumberToTime(1))
    return entries.filter((e) => e.dataKey !== EXTENSION_FORECAST_PREV_KEY)
  if (label === komaNumberToTime(48))
    return entries.filter((e) => e.dataKey !== EXTENSION_FORECAST_NEXT_KEY)
  return [...entries]
}

/** 気温・露点 (℃、右軸) の系列 key か。 */
export function isTemperatureSeriesKey(dataKey: string | undefined): boolean {
  if (dataKey === undefined) return false
  return (
    dataKey === 'tempForecast' ||
    dataKey === 'tempActual' ||
    dataKey === 'dewActual' ||
    dataKey.startsWith('pastTemp_') ||
    dataKey.startsWith('pastDew_')
  )
}

/**
 * ツールチップに出す表示値。kWh 系列は四捨五入 + 千区切り (formatKwh)、
 * ℃ 系列は値をそのまま文字列化する。
 */
export function formatChartTooltipValue(
  dataKey: string | undefined,
  value: number,
): string {
  return isTemperatureSeriesKey(dataKey) ? String(value) : formatKwh(value)
}
